import React from "react";
import { Order } from "../../types";
import { EditableInfoRow, InfoRow } from "./InfoRow";
import { formatDate } from "src/shared/components/dateUtils";
import PaymentForm from "./PaymentForm";
import { notifyError } from "src/assets/toastService";

interface CustomerInfoProps {
  orderData: Order | null;
  onReceivedAmountChange: (amount: number) => void;
  receivedAmount: number;
  remainingAmount: number;
  onCancel: () => void;
  onConfirm: () => void;
}

const formatCurrency = (value: number | string | undefined): string => {
  if (value === undefined) return "0.00";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

const CustomerInfo: React.FC<CustomerInfoProps> = ({ orderData, onReceivedAmountChange, receivedAmount, remainingAmount, onCancel, onConfirm }) => {
  const totalPrice = Number(orderData?.totalOrderPrice) || 0;
  const remainingAmountClass = remainingAmount > 0 ? "text-orange-600 font-semibold" : "text-green-600 font-semibold";
  const isCashPayment = orderData?.payment?.paymentMethod === "cash";
  const isBuyOnCreditPayment = orderData?.payment?.paymentMethod === "Buy on credit";

  const isConfirmDisabled = isCashPayment ? receivedAmount <= 0 : false;

  const handleAmountChange = (amount: number) => {
    if (amount > totalPrice) {
      notifyError(`Received amount cannot exceed total price (₹${formatCurrency(totalPrice)})`);
      onReceivedAmountChange(totalPrice);
    } else {
      onReceivedAmountChange(amount);
    }
  };

  const carNumberCount = orderData?.serviceDetails?.length || 0;
  const carNumbers = orderData?.serviceDetails?.map((service) => service.carNumber) || [];

  React.useEffect(() => {
    if (isBuyOnCreditPayment) {
      onReceivedAmountChange(0);
    }
  }, [isBuyOnCreditPayment, onReceivedAmountChange]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-2">
      <h3 className="text-xl font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-200">Customer Information</h3>

      <div className="space-y-4">
        <InfoRow label="Customer name" value={orderData?.Name || "-"} />

        <InfoRow
          label="Car number"
          value={
            carNumberCount > 0 ? (
              <div className="inline-block relative group" title={carNumbers.join(", ")}>
                <span className="cursor-help underline decoration-dotted">{`${carNumberCount} car${carNumberCount !== 1 ? "s" : ""}`}</span>
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 whitespace-nowrap">
                  <div className="font-semibold mb-1">No Of Cars:</div>
                  <div>{carNumbers.join(", ")}</div>
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                </div>
              </div>
            ) : (
              "-"
            )
          }
        />

        <InfoRow label="Phone number" value={orderData?.phone_number || "-"} />
        <InfoRow label="Date" value={formatDate(orderData?.date || "-")} />
        <InfoRow label="PaymentMethod" value={orderData?.payment?.paymentMethod || "-"} />
        <InfoRow label="Total price" value={`${formatCurrency(orderData?.totalOrderPrice)}`} />
        {isCashPayment && <EditableInfoRow label="Received amount" value={receivedAmount} onChange={handleAmountChange} max={totalPrice} />}
        {isBuyOnCreditPayment && <InfoRow label="Received amount" value={`${formatCurrency(0)}`} valueClassName="text-gray-600" />}
        <InfoRow label="Remaining amount" value={`${formatCurrency(remainingAmount)}`} valueClassName={remainingAmountClass} />
      </div>

      <PaymentForm onCancel={onCancel} onConfirm={onConfirm} isConfirmDisabled={isConfirmDisabled} />
    </div>
  );
};

export default CustomerInfo;
